import {
  Chart as ChartJS,
  CategoryScale,
  Filler,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js"
import { Chart } from "react-chartjs-2"
import { Skeleton } from "@mui/material"
import { colors } from "../../services/config/colors"
import { formatNumber } from "../../services/utils/utils"

ChartJS.register(
  CategoryScale,
  Filler,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip
)

export interface MobilityLineChartDataset {
  label: string
  data: number[]
  color: string
  // "y1" draws the dataset against a second axis on the right
  yAxisID?: "y" | "y1"
}

const axisTitle = (text?: string) => ({
  display: !!text,
  text: text ?? "",
  color: colors.primary,
  font: { family: "Satoshi", size: 12, weight: 500 as const },
})

// "#RRGGBB" to "rgba(r, g, b, alpha)"
const withAlpha = (hex: string, alpha: number) => {
  const value = parseInt(hex.replace("#", ""), 16)
  return `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${
    value & 255
  }, ${alpha})`
}

// line or area chart per period, as the Looker Studio line, area and sparkline charts:
// "line" draws plain lines (optionally on two axes), "area" fills each series down to
// the axis (overlapping) and "stacked" stacks the filled series on top of each other
const MobilityLineChart = ({
  labels,
  datasets,
  variant = "line",
  compact = false,
  yTitle,
  y1Title,
  yMax,
  loading,
}: {
  labels: string[]
  datasets: MobilityLineChartDataset[]
  variant?: "line" | "area" | "stacked"
  compact?: boolean
  yTitle?: string
  y1Title?: string
  yMax?: number
  loading: boolean
}) => {
  const withSecondAxis = datasets.some((item) => item.yAxisID === "y1")
  const stacked = variant === "stacked"

  const ticks = {
    color: colors.textSecondary,
    font: { family: "Satoshi", size: compact ? 11 : 14 },
  }

  return loading ? (
    <Skeleton variant="rounded" style={{ width: "100%", height: "100%" }} />
  ) : (
    <Chart
      type="line"
      style={{ maxWidth: "100%" }}
      data={{
        labels,
        datasets: datasets.map((item, index) => ({
          label: item.label,
          data: item.data,
          borderColor: item.color,
          backgroundColor:
            variant === "line"
              ? item.color
              : withAlpha(item.color, stacked ? 0.85 : 0.3),
          // sparklines get a light area under the line
          fill:
            variant === "stacked"
              ? index === 0
                ? "origin"
                : "-1"
              : variant === "area" || compact
              ? "origin"
              : false,
          yAxisID: item.yAxisID ?? "y",
          borderWidth: compact ? 2 : 3,
          pointRadius: compact ? 0 : 2.4,
          pointHoverRadius: 4,
          cubicInterpolationMode: "monotone" as const,
        })),
      }}
      options={{
        interaction: { mode: "index", intersect: false },
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            border: { color: colors.chartsGrid },
            grid: { display: false },
            ticks: {
              ...ticks,
              maxRotation: 0,
              autoSkip: true,
              display: !compact,
            },
          },
          y: {
            stacked,
            min: 0,
            max: yMax,
            display: !compact,
            border: { width: 0 },
            grid: { color: colors.chartsGrid, tickWidth: 0 },
            ticks,
            title: axisTitle(yTitle),
          },
          ...(withSecondAxis
            ? {
                y1: {
                  position: "right" as const,
                  min: 0,
                  border: { width: 0 },
                  grid: { display: false },
                  ticks,
                  title: axisTitle(y1Title),
                },
              }
            : {}),
        },
        plugins: {
          datalabels: { display: false },
          legend: { display: false },
          title: { display: false },
          filler: { propagate: false },
          tooltip: {
            enabled: true,
            callbacks: {
              label: (item) =>
                `${item.dataset.label}: ${formatNumber(item.raw as number)}`,
            },
          },
        },
      }}
    />
  )
}

export default MobilityLineChart
